import React from 'react'
import { useCurrentPng } from 'recharts-to-png'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
  Label,
} from 'recharts'
import { BAR_CHART_DATA_POINT_WIDTH } from 'constants/chartConstants'
import { ChartDownloadButton, CustomBar, Loader } from 'components'
import { formatNumber, formatDateString, FormatNumberOptions } from 'utils/utilities'

export type ComparisonBarChartProps = {
  title: string
  subTitle?: string
  data: any[]
  labelX?: string
  xAxisKey: string
  bar1Label: string
  bar2Label?: string
  bar1Key: string
  bar2Key?: string
  bar1Color?: string
  bar2Color?: string
  bar1Format?: FormatNumberOptions
  bar2Format?: FormatNumberOptions
  singleSize?: boolean
}

export const ComparisonBarChart = (props: ComparisonBarChartProps) => {
  const {
    title,
    subTitle = '',
    data,
    labelX,
    xAxisKey,
    bar1Label,
    bar2Label,
    bar1Key,
    bar2Key,
    bar1Color = '#ffc20e',
    bar2Color = '#256bb3',
    bar1Format,
    bar2Format,
    singleSize,
  } = props
  const [calculatedWidth, setCalculatedWidth] = React.useState(0)

  const measuredRef = React.useCallback(
    (node: HTMLDivElement) => {
      if (node !== null) {
        const clientWidth = node.getBoundingClientRect().width
        const dataWidth = data.length * BAR_CHART_DATA_POINT_WIDTH
        setCalculatedWidth(dataWidth < clientWidth ? clientWidth : dataWidth)
      }
    },
    [data.length]
  )

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
      return (
        <div className="bg-white-95 p-4">
          <div className={'text-xl'}>{formatDateString(label, undefined, 'dd MMM yy')}</div>
          <p style={{ color: bar1Color }}>{bar1Label}</p>
          <p>{formatNumber(payload[0]?.payload?.[bar1Key], bar1Format) ?? '-'}</p>
          {bar2Key && (
            <>
              <p style={{ color: bar2Color }}>{bar2Label}</p>
              <p>{formatNumber(payload[0]?.payload?.[bar2Key], bar2Format) ?? '-'}</p>
            </>
          )}
        </div>
      )
    }
    return null
  }

  const [getPng, { ref, isLoading }] = useCurrentPng()

  return (
    <div className="mt-10 max-w-full">
      {isLoading && <Loader />}
      <div className="flex justify-end">
        <ChartDownloadButton getPng={getPng} fileName={`${title}_${subTitle}`} />
      </div>
      <div className={`relative pt-10 bg-gray-100 ${singleSize ? 'max-w-lg' : ''}`}>
        <div className={`flex flex-col items-center text-primary`}>
          <span className="mx-2 text-2xl">{title}</span>
          <span className="text-lg underline">{subTitle}</span>
        </div>
        <div className="overflow-x-auto" ref={measuredRef}>
          {!!calculatedWidth && (
            <ResponsiveContainer minWidth={singleSize ? 400 : calculatedWidth} minHeight={420} debounce={1}>
              <BarChart
                ref={ref}
                data={data}
                margin={{
                  top: 30,
                  bottom: 20,
                  right: 40,
                  left: 5,
                }}
                barSize={singleSize ? 60 : 20}
                stackOffset={'sign'}
              >
                <XAxis
                  dataKey={xAxisKey}
                  orientation="top"
                  tick={{ fill: 'green', fontSize: 18 }}
                  stroke="green"
                  strokeWidth={3}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => formatDateString(value, undefined, 'dd MMM yy')}
                >
                  {labelX && <Label value={labelX} position="insideRight" dy={-30} fill="#00683A" fontSize="18px" />}
                </XAxis>
                <YAxis
                  stroke="green"
                  strokeWidth={3}
                  tickLine={false}
                  tickFormatter={(value) => formatNumber(value, { format: 'abbr' }) ?? value}
                  interval={'preserveStartEnd'}
                ></YAxis>
                <Tooltip content={<CustomTooltip />} filterNull={false} />
                <CartesianGrid horizontal={true} vertical={false} stroke="green" strokeWidth={0.5} />
                <Bar
                  dataKey={bar1Key}
                  fill={bar1Color}
                  shape={<CustomBar dataKey={bar1Key} minThickness={5} />}
                  name={bar1Label}
                />
                {bar2Key && (
                  <Bar
                    dataKey={bar2Key}
                    fill={bar2Color}
                    shape={<CustomBar dataKey={bar2Key} minThickness={5} />}
                    name={bar2Label}
                  />
                )}
                <ReferenceLine stroke="green" y={0} strokeWidth={2} />
                <Legend
                  wrapperStyle={{
                    left: '0',
                    right: '0',
                    bottom: '15px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                  iconSize={20}
                  formatter={(value) => <span className="text-primary-tint text-lg">{value}</span>}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  )
}
